import React, { ComponentType, useContext, useEffect } from 'react';

import * as Sentry from '@sentry/nextjs';
import { stringify as queryStringify } from 'querystring';
import {
  GetServerSideProps,
  GetServerSidePropsContext,
  GetServerSidePropsResult,
} from 'next';
import { AxiosError } from 'axios';

import { refresh } from '@api/auth';

import { initStorageEventHandler } from '@utils/local-storage';
import { isTokenInvalidError, setLogoutHandler } from '@utils/api-client';
import { isOnLoginPage } from '@utils/location';

import { Loader } from '@atoms';

import { AccountAuthPayload, AuthContext } from './auth-context';
import {
  createLoginRedirectUrl,
  getTokenFromRequest,
  getTokenPayload,
} from './utils';

import { loginReasons, Role } from '.';

type AuthOptions = {
  userRequired?: boolean;
  shouldHandleRefreshToken?: boolean;
  allowedUserRoles: string[];
};

type DynamicAuthOptions = (ctx: GetServerSidePropsContext) => AuthOptions;

type AuthState = {
  isAuthenticated: boolean;
  account?: AccountAuthPayload | undefined;
  reason?: string;
  authOptions: AuthOptions;
};

type WithAuthState<T = {}> = Omit<T, 'authState'> & { authState: AuthState };

export type Component<
  P extends (Record<any, any> & { authState?: never }) | null = null,
  CP = {},
> = P extends {} ? ComponentWithServerSideProps<P, CP> : ComponentType<CP>;

type ComponentWithServerSideProps<
  P extends Record<any, any> = {},
  CP = {},
> = ComponentType<CP & P> & {
  getServerSideProps: GetServerSideProps<P>;
};

/**
 * Wrapper that will handle both authentication & authorization
 * of the user. If wrapped component uses `getServerSideProps`,
 * it gets called AFTER authorization is complete & successful.
 *
 *  IMPORTANT: Default props defined in the Component props will be overwritten!
 *  Use `defaultProps` parameter for default values instead.
 *
 * @template P Used for props that are returned by `getServerSideProps` function
 * @template CP Used for props that defined by `defaultProps` variable
 *
 * @param Component - Page component
 * @param authOptions - Options that specifies what kind of authorization users need to visit this page
 * @param defaultProps - Default page component props
 */
export function auth(
  Component: Component,
  authOptions: AuthOptions | DynamicAuthOptions,
): {
  SecuredComponent: ComponentType<WithAuthState>;
  getServerSideProps: GetServerSideProps<WithAuthState>;
};
export function auth<P extends Record<any, any> & { authState?: never }>(
  Component: Component<P>,
  authOptions: AuthOptions | DynamicAuthOptions,
): {
  SecuredComponent: ComponentType<WithAuthState<P>>;
  getServerSideProps: GetServerSideProps<WithAuthState<P>>;
};
export function auth<P extends Record<any, any> & { authState?: never }, CP>(
  Component: Component<P, CP>,
  authOptions: AuthOptions | DynamicAuthOptions,
  defaultProps: CP,
): {
  SecuredComponent: ComponentType<WithAuthState<P>>;
  getServerSideProps: GetServerSideProps<WithAuthState<P>>;
};
export function auth<
  P extends (Record<any, any> & { authState?: never }) | null,
  CP,
>(
  Component: Component<P, CP>,
  authOptions: AuthOptions | DynamicAuthOptions,
  defaultProps: CP,
): {
  SecuredComponent: ComponentType<WithAuthState<P>>;
  getServerSideProps: GetServerSideProps<WithAuthState<P>>;
};
export function auth<
  P extends Record<any, any> & { authState?: never } = {},
  CP extends Record<any, any> = {},
>(
  Component: Component<P, CP>,
  authOptions: AuthOptions | DynamicAuthOptions = {
    userRequired: true,
    allowedUserRoles: [],
  },
  defaultProps?: CP,
): {
  SecuredComponent: ComponentType<WithAuthState<P>>;
  getServerSideProps: GetServerSideProps<WithAuthState<P>>;
} {
  const SecuredComponent: ComponentType<WithAuthState<P>> = ({
    authState: { authOptions },
    ...props
  }) => {
    const { account, ...authCtx } = useContext(AuthContext);

    useEffect(() => {
      // prevent refreshing of the access token on mount,
      // if authentication is not required
      if (
        typeof authOptions.shouldHandleRefreshToken === 'boolean' &&
        !authOptions.shouldHandleRefreshToken
      ) {
        authCtx.setChecked(true);
        return;
      }

      // init of local storage events (to sync state between tabs)
      const listener = initStorageEventHandler({
        handleLoginRedirect: authCtx.handleLoginRedirect,
        handleLoggedInRedirect: authCtx.handleLoggedInRedirect,
      });
      setLogoutHandler(authCtx.handleLoginRedirect);
      window.addEventListener('storage', listener, false);

      if (account) {
        authCtx.setChecked(true);
        return;
      }

      (async () => {
        let showContent = true;
        try {
          const { account } = await refresh();
          authCtx.setAccount(account);
        } catch (e: any) {
          // apiClient handler is redirecting to the log-in page, show loader
          if (
            isTokenInvalidError(e, [
              'Refresh token is about to expire',
              'Session does not recognize this ip address',
              'Session was not found',
            ])
          ) {
            // show content only if we are on login page (api client will cleanup account state & auth cookies, but will not redirect user to the login page, since he is already there)
            showContent = isOnLoginPage();
          } else if (
            !isTokenInvalidError(e) &&
            (e as AxiosError).response?.status !== 400
          )
            // error is not related to the token, log error
            Sentry.captureException(e);
        } finally {
          authCtx.setChecked(showContent);
        }
      })();

      return () => {
        setLogoutHandler(undefined);
        window.removeEventListener('storage', listener);
      };
    }, []);

    // refresh access token logic
    useEffect(() => {
      if (
        !account ||
        (typeof authOptions.shouldHandleRefreshToken === 'boolean' &&
          !authOptions.shouldHandleRefreshToken)
      )
        return;

      const expiresIn = account.exp * 1000 - Date.now();
      const interval = setInterval(async () => {
        let account;
        try {
          ({ account } = await refresh());
        } catch (e: any) {
          if (!isTokenInvalidError(e)) Sentry.captureException(e);
        }
        if (account) authCtx.setAccount(account);
      }, Math.floor(expiresIn / 2));
      return () => {
        clearInterval(interval);
      };
    }, [account]);

    if (authOptions?.shouldHandleRefreshToken && !authCtx.isChecked) {
      return <Loader style={{ height: '100%' }} />;
    }

    return (
      <Component
        {...{
          ...defaultProps,
          // ts does not like that we are ommitting that auth state prop,
          // but it should not matter, since we are prohibiting Components
          // props with { authState?: never }
          // DO NOT ATTEMPT TO FIX THIS SHIT - Tomaso February 2023
          ...(props as any),
        }}
      />
    );
  };

  let getServerSideProps = {} as GetServerSideProps<WithAuthState<P>>;
  // this is NEEDED so this code is not available on FE
  if (typeof window === 'undefined') {
    const protocol = ['staging', 'production'].includes(
      process.env.APP_ENV ?? '',
    )
      ? 'https'
      : 'http';

    getServerSideProps = async (ctx) => {
      const auth =
        typeof authOptions === 'function' ? authOptions(ctx) : authOptions;
      const url = new URL(
        `${protocol}://${ctx.req.headers.host}${ctx.resolvedUrl}`,
      );

      const token = getTokenFromRequest(ctx.req);
      const account = token ? getTokenPayload(token) : null;
      const authStatus = getAuthStatus(account, auth);

      // account is not authorized to view this page,
      // send him to login
      if (!authStatus.isAuthenticated) {
        const destination = createLoginRedirectUrl(
          queryStringify(ctx.query),
          url,
          authStatus.reason,
        );

        return {
          redirect: {
            statusCode: 302,
            destination,
          },
        };
      }

      if (!('getServerSideProps' in Component)) {
        // at this point, we know that return type of this
        // server side render should be just AuthState,
        // because `getServerSideProps` function was not specified
        return {
          props: {
            authState: { ...authStatus, authOptions: auth },
          } as unknown as WithAuthState<P>,
        };
      }

      const componentProps: GetServerSidePropsResult<P> =
        await Component.getServerSideProps(ctx).catch((e) => {
          if ((e as AxiosError).isAxiosError) {
            const axiosError = e as AxiosError;
            if (axiosError.response?.status === 401) {
              const destination = createLoginRedirectUrl(
                queryStringify(ctx.query),
                url,
                loginReasons.session_expired,
              );
              return {
                redirect: {
                  statusCode: 302,
                  destination,
                },
              };
            }
          }
          // Throw 404 because of the error
          return { notFound: true };
        });

      if ('props' in componentProps) {
        // Components getServerSideProps function was successful,
        // return props with authState
        return {
          props: {
            // getServerSideProps could return props inside Promise
            ...(await componentProps.props),
            authState: { ...authStatus, authOptions: auth },
          },
        };
      }

      return componentProps;
    };

    const getAuthStatus = (
      account: AccountAuthPayload,
      { userRequired, allowedUserRoles }: AuthOptions,
    ): Omit<AuthState, 'authOptions'> => {
      // user is not required
      if (!userRequired) return { isAuthenticated: true, account };

      // user is required and not logged
      if (userRequired && !account)
        return { isAuthenticated: false, reason: loginReasons.login_required };

      // check user roles
      if (account && allowedUserRoles.length) {
        const userHasRequiredRoles = allowedUserRoles.some((role) =>
          account.roles.includes(role as Role),
        );
        return {
          isAuthenticated: userHasRequiredRoles,
          account,
          ...(!userHasRequiredRoles && { reason: loginReasons.missing_role }),
        };
      } else if (account && !allowedUserRoles.length) {
        return { isAuthenticated: true, account };
      }

      return {
        isAuthenticated: false,
        account,
      };
    };
  }

  return { SecuredComponent, getServerSideProps };
}
